import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MIRROR_PHOTO } from '../content/weeklyClaimLens';
import './MirrorRoomPage.css';

type PathId = 'observe' | 'imagine' | 'verify';

const paths: Record<PathId, {
    number: string;
    label: string;
    question: string;
    intro: string;
    insight: string;
    prompt: string;
    links: { label: string; href: string }[];
}> = {
    observe: {
        number: '01', label: '관찰하기', question: '이 사진에서 실제로 보이는 것은?',
        intro: '거울, 줄무늬 양말 한 짝, 빈 종이와 연필은 사진에서 볼 수 있습니다. 양말의 주인이나 다른 한 짝의 행방은 보이지 않습니다.',
        insight: '사진은 장면을 보여주지만 이유까지 알려주지는 않습니다. 보이는 물건 한 가지와 아직 알 수 없는 점 한 가지를 분리해 보세요.',
        prompt: '사진 댓글에 “내가 본 것 / 궁금한 것”을 한 줄씩 남겨볼까요?',
        links: [{ label: '사진에서 의견 나누기 ↗', href: MIRROR_PHOTO.href }, { label: '사진 관찰실 열기 ↗', href: '#/observation' }]
    },
    imagine: {
        number: '02', label: '상상하기', question: '한 짝의 양말에 어떤 이야기를 붙일까?',
        intro: '최근 커뮤니티에는 거울 속 평행 세계와 양말의 차원 이동을 상상한 글이 올라왔습니다. 재미있는 설정으로 읽되 실제 사건의 증거로 받아들이지는 않아도 됩니다.',
        insight: '“다른 한 짝이 거울 너머로 갔다면?”처럼 가정 하나를 세우면 짧은 이야기가 시작됩니다. 다음 장면은 글쓴이의 원문에서 이어 보세요.',
        prompt: '양말이 돌아온다면 무엇을 가지고 올까요? 내 상상을 원문 의견에 보태보세요.',
        links: [{ label: '양말 이야기 읽기 ↗', href: '#/board?post=250' }, { label: '거울 이야기 읽기 ↗', href: '#/board?post=252' }]
    },
    verify: {
        number: '03', label: '확인하기', question: '거울 속 움직임이 늦는다면 무엇을 확인할까?',
        intro: '거울 이야기에는 눈 깜빡임이 늦어지는 듯한 장면이 등장합니다. 사진 한 장만으로 움직임이나 원인을 확인할 수는 없습니다.',
        insight: '움직임을 살피려면 연속된 기록이 필요합니다. 같은 각도에서 반복해 보고 촬영·재생 지연 같은 다른 설명도 검토할 수 있습니다. 여기서 현상의 존재를 단정하지 않습니다.',
        prompt: '먼저 확인하고 싶은 조건은 무엇인가요? 원문에서 다른 사람의 생각과 비교해 보세요.',
        links: [{ label: '거울 원문에서 대화하기 ↗', href: '#/board?post=252' }, { label: '3문장 읽기로 연습하기 ↗', href: '#/lens' }]
    }
};

const isPathId = (value: string | null): value is PathId =>
    value === 'observe' || value === 'imagine' || value === 'verify';

export default function MirrorRoomPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [shareMessage, setShareMessage] = useState('');
    const pathId = isPathId(searchParams.get('path')) ? searchParams.get('path') as PathId : null;
    const selected = pathId ? paths[pathId] : null;

    useEffect(() => {
        const priorTitle = document.title;
        const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
        const priorDescription = description?.content;
        document.title = '거울 앞의 한 짝 · 이번 주 질문 | TruthPick';
        if (description) description.content = '운영자 제작 사진과 실제 커뮤니티의 거울·양말 이야기를 보고, 관찰·상상·확인 중 나의 질문을 골라보세요.';
        return () => {
            document.title = priorTitle;
            if (description && priorDescription !== undefined) description.content = priorDescription;
        };
    }, []);

    useEffect(() => {
        if (!selected) return;
        const timer = window.setTimeout(() => document.getElementById('mirror-answer')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }), 0);
        return () => window.clearTimeout(timer);
    }, [pathId, selected]);

    const choose = (id: PathId) => {
        setShareMessage('');
        setSearchParams({ path: id });
    };

    const share = async () => {
        if (!pathId || !selected) return;
        const url = `${window.location.origin}/mirror.html?path=${pathId}`;
        try {
            if (navigator.share) {
                await navigator.share({ title: selected.question, text: `TruthPick에서 내가 고른 질문: ${selected.question}`, url });
                setShareMessage('공유했습니다.');
            } else if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(`${selected.question}\n${url}`);
                setShareMessage('질문과 직접 링크를 복사했습니다.');
            } else setShareMessage(`직접 링크: ${url}`);
        } catch { setShareMessage(`직접 링크: ${url}`); }
    };

    return <main className="mirror-page">
        <header className="mirror-hero"><div className="mirror-hero-copy"><span className="mirror-kicker">THIS WEEK / 2026.10.31</span><h1>같은 장면에서<br />다른 질문이 시작됩니다.</h1><p>거울과 양말 한 짝. 먼저 사진을 보고 마음이 가는 질문 하나를 고르세요. 가입 없이 바로 읽을 수 있고 정답을 맞힐 필요도 없습니다.</p><button type="button" onClick={() => document.getElementById('mirror-choices')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })}>질문 고르기 ↓</button></div><figure><img src={MIRROR_PHOTO.imageUrl} alt={MIRROR_PHOTO.alt} /><figcaption>운영자 제작 연출 이미지 · 실제 사건의 증거가 아닙니다.</figcaption></figure></header>
        <section className="mirror-context"><div><span>FROM THE COMMUNITY</span><h2>최근 올라온 상상에서 출발했어요.</h2></div><p><a href="#/board?post=252">거울 속 또 다른 나 ↗</a>와 <a href="#/board?post=250">사라진 양말 ↗</a>은 실제 커뮤니티 글입니다. 글의 내용을 사실로 단정하지 않고, 운영자가 만든 사진과 함께 새로운 질문으로 이어 읽습니다.</p></section>
        <section className="mirror-choices" id="mirror-choices" aria-label="질문 선택"><div className="mirror-section-head"><span>CHOOSE YOUR QUESTION</span><h2>어느 쪽이 궁금한가요?</h2><p>하나만 눌러도 바로 다음 읽을거리가 열립니다.</p></div><div className="mirror-choice-grid">{(Object.keys(paths) as PathId[]).map((id) => <button key={id} type="button" onClick={() => choose(id)} className={pathId === id ? 'active' : ''} aria-pressed={pathId === id}><span>{paths[id].number} / {paths[id].label}</span><strong>{paths[id].question}</strong><small>이 질문으로 읽기 ↗</small></button>)}</div></section>
        {selected && <section className="mirror-answer" id="mirror-answer" aria-live="polite"><div className="mirror-answer-heading"><span>YOUR READING / {selected.number}</span><h2>{selected.question}</h2><p>{selected.intro}</p></div><div className="mirror-answer-body"><div><span>한 걸음 더</span><p>{selected.insight}</p></div><div><span>이어서 할 일</span><p>{selected.prompt}</p><div className="mirror-answer-links">{selected.links.map((link) => <a key={link.href} href={link.href}>{link.label}</a>)}</div></div></div><div className="mirror-answer-actions"><button type="button" onClick={share}>이 질문 공유하기 ↗</button><button type="button" onClick={() => { setShareMessage(''); document.getElementById('mirror-choices')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }); }}>다른 질문도 고르기 ↑</button></div>{shareMessage && <p role="status" className="mirror-share-message">{shareMessage}</p>}</section>}
        <footer className="mirror-footer"><span>KEEP EXPLORING</span><h2>사진 한 장에서 시작한 질문을<br />대화로 이어가 보세요.</h2><a href={MIRROR_PHOTO.href}>이번 주 사진과 의견 보기 ↗</a><a href="#/board">모든 이야기 둘러보기 ↗</a></footer>
    </main>;
}
